import { getBackorderState } from './bb-backorder-state.js?v=923986c9a2fb';
import { backorderProperties, normalizeProperties, sameProperties } from './bb-backorder-properties.js?v=fe956ef205c6';

// Storefront disclosure only. Shopify Advanced does not provide a custom-app
// server validator. Direct checkout/API requests can bypass a theme checkbox.
const shopRoot = window.Shopify?.routes?.root || '/';
const cartUrl = `${shopRoot}cart`;
const scopes = '[data-bb-product-scope]';
const managedForms = '[data-bb-product-form]';
let currentState = null;
let acceptedContext = null;
let checkoutBusy = false;
let refreshTimer;
let refreshVersion = 0;
let renderScheduled = false;
const replayControls = new WeakSet();
const replayForms = new WeakSet();
const lockedCartInputs = new Map();

function lockCartInputs() {
  document.querySelectorAll('.cart-item__quantity-input').forEach((input) => {
    if (!lockedCartInputs.has(input)) lockedCartInputs.set(input, input.disabled);
    input.disabled = true;
  });
}

function restoreCartInputs() {
  lockedCartInputs.forEach((wasDisabled, input) => {
    // The theme disables fading-out obsolete rows so their updates[] inputs
    // cannot participate in native cart submission. Do not re-enable them.
    if (input.isConnected && !input.closest('.merge-remove-item')) input.disabled = wasDisabled;
  });
  lockedCartInputs.clear();
}

function resolveCheckoutControl(original) {
  if (!original) return () => null;
  const cart = original.closest('cart-form');
  const section = original.closest('.shopify-section');
  const form = original.form;
  const tag = original.tagName;
  const name = original.name;
  const href = original instanceof HTMLAnchorElement ? original.href : null;
  return () => {
    if (original.isConnected) return original;
    const root = (cart?.isConnected && cart) || (section?.isConnected && section) ||
      (section?.id && document.getElementById(section.id)) || (form?.isConnected && form);
    const replacement = root && Array.from(root.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href]'))
      .find((candidate) => candidate.tagName === tag &&
        (href ? candidate.href === href : candidate.name === name));
    if (!replacement) throw new Error('Your cart has updated. Please use the checkout button again.');
    return replacement;
  };
}

function replayCheckout(control, fallbackForm) {
  const form = control?.form || fallbackForm;
  if (control) replayControls.add(control);
  if (form) replayForms.add(form);
  try {
    if (control) control.click();
    else if (form?.isConnected) form.requestSubmit();
    else throw new Error('Your cart has updated. Please use the checkout button again.');
  } finally {
    if (control) replayControls.delete(control);
    if (form) replayForms.delete(form);
  }
}

function showError(message, focus = false) {
  document.querySelectorAll('[data-bb-backorder-error]').forEach((el) => {
    el.textContent = message;
    el.hidden = !message;
    if (message) el.closest('[data-bb-backorder-ack]').hidden = false;
  });
  if (focus) {
    const checkbox = Array.from(document.querySelectorAll('[data-bb-backorder-accept]'))
      .find((el) => el.getClientRects().length && !el.disabled);
    checkbox?.focus();
    checkbox?.closest('[data-bb-backorder-ack]')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

function updateNotice(notice, line) {
  if (!notice || !line) return;
  notice.hidden = line.backorderQty <= 0;
  notice.dataset.variantId = line.variantId;
  notice.dataset.stockQuantity = String(line.inventoryQuantity ?? 0);
  notice.dataset.backorderQuantity = String(line.backorderQty);
  notice.dataset.quantity = String(line.quantity);
  notice.dataset.warehouseEta = line.warehouseEta || '';
  const eta = notice.querySelector('[data-bb-backorder-eta]');
  if (eta) eta.textContent = line.warehouseEta || 'To be confirmed';
  const summary = notice.querySelector('[data-bb-backorder-summary]');
  if (summary) summary.textContent = line.backorderQty < line.quantity
    ? `Backordered quantity: ${line.backorderQty} of ${line.quantity}.`
    : 'This item is currently out of stock.';
}

function readVariant(record) {
  return {
    variantId: record.dataset.variantId,
    tracked: record.dataset.inventoryTracked === 'true',
    policy: record.dataset.continueSelling === 'true' ? 'continue' : 'deny',
    inventoryQuantity: Number(record.dataset.stockQuantity),
    warehouseEta: record.dataset.warehouseEta || '',
  };
}

function updateProduct(scope) {
  const form = scope.querySelector(managedForms);
  if (!form) return;
  const variantId = String(new FormData(form).get('id') || '');
  const record = Array.from(scope.querySelectorAll('[data-bb-backorder-variant]'))
    .find((el) => el.dataset.variantId === variantId);
  const notice = form.querySelector('[data-bb-backorder-notice]');
  if (!record) {
    if (notice) notice.hidden = true;
    scope.dataset.bbHasBackorder = 'false';
    form.querySelectorAll('[data-bb-property]').forEach((el) => el.remove());
    return;
  }
  const variant = readVariant(record);
  const quantity = Math.max(1, Math.floor(Number(new FormData(form).get('quantity') || 1)) || 1);
  const existing = currentState?.lines.filter((line) => line.variantId === variantId)
    .reduce((total, line) => total + line.quantity, 0) || 0;
  const remaining = Math.max(0, variant.inventoryQuantity - existing);
  const backorderQty = variant.tracked && variant.policy === 'continue'
    ? Math.min(quantity, Math.max(0, quantity - remaining)) : 0;
  const line = { ...variant, quantity, backorderQty };
  updateNotice(notice, line);
  scope.dataset.bbHasBackorder = String(backorderQty > 0);
  const button = form.querySelector('[data-bb-add-button]');
  if (button && !button.disabled && !button.classList.contains('btn--in-progress')) {
    const label = backorderQty > 0 ? 'Order on backorder' : button.dataset.bbDefaultLabel;
    if (label && button.textContent.trim() !== label) button.textContent = label;
  }
  const props = backorderQty > 0 ? backorderProperties(line) : {};
  const oldInputs = new Map(Array.from(form.querySelectorAll('[data-bb-property]')).map((el) => [el.name, el]));
  for (const [name, value] of Object.entries(props)) {
    const inputName = `properties[${name}]`;
    let input = oldInputs.get(inputName);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = inputName;
      input.dataset.bbProperty = '';
      form.append(input);
    }
    input.value = value;
    oldInputs.delete(inputName);
  }
  oldInputs.forEach((el) => el.remove());
}

function renderProducts() {
  document.querySelectorAll(scopes).forEach(updateProduct);
}

function renderCart() {
  if (!currentState) return;
  const accepted = acceptedContext === currentState.context;
  document.querySelectorAll('[data-bb-backorder-ack]').forEach((root) => {
    root.hidden = !currentState.requiresAcknowledgement;
    const checkbox = root.querySelector('[data-bb-backorder-accept]');
    if (checkbox) {
      checkbox.disabled = !currentState.requiresAcknowledgement;
      checkbox.required = currentState.requiresAcknowledgement;
      checkbox.setAttribute('aria-required', String(currentState.requiresAcknowledgement));
      checkbox.checked = currentState.requiresAcknowledgement && accepted;
    }
    const summary = root.querySelector('[data-bb-backorder-summary]');
    if (summary) {
      const text = currentState.backorderLines.map((line) =>
        `${line.title}: ${line.backorderQty} on backorder. AU warehouse arrival: ${line.warehouseEta || 'date to be confirmed'}.`
      ).join('\n');
      if (summary.textContent !== text) summary.textContent = text;
    }
  });
  document.querySelectorAll('[data-bb-accelerated-checkout]').forEach((el) => {
    el.hidden = currentState.requiresAcknowledgement;
  });
  document.querySelectorAll('[data-bb-cart-line-key]').forEach((row) => {
    const indexed = currentState.lines[Number(row.dataset.bbCartLineIndex) - 1];
    const line = indexed?.key === row.dataset.bbCartLineKey ? indexed
      : currentState.lines.find((item) => item.key === row.dataset.bbCartLineKey);
    if (line) updateNotice(row.querySelector('[data-bb-backorder-notice]'), line);
  });
  renderProducts();
}

function useState(state) {
  if (currentState && currentState.context !== state.context) acceptedContext = null;
  currentState = state;
  renderCart();
}

async function readSnapshot() {
  const response = await fetch(`${cartUrl}?view=bb-backorders&_bb=${Date.now()}`, {
    // application/json makes Shopify bypass the Liquid view and return its
    // native cart JSON, which has no inventory/metafield disclosure snapshot.
    credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' },
  });
  if (!response.ok) throw new Error('Unable to refresh stock availability.');
  const data = await response.json();
  if (!Array.isArray(data.lines)) throw new Error('Invalid availability response.');
  return getBackorderState(data.lines.map((line) => ({
    ...line, properties: normalizeProperties(line.properties),
  })));
}

async function refreshCart() {
  const version = ++refreshVersion;
  const state = await readSnapshot();
  if (version === refreshVersion) useState(state);
  return state;
}

function queueRefresh() {
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    if (!checkoutBusy) refreshCart().catch(() => {});
  }, 120);
}

function cartIsChanging() {
  return Boolean(document.querySelector('cart-form.cart-form--refreshing, .merge-item-refreshing, cart-form .merge-remove-item')) ||
    Array.from(document.querySelectorAll('.cart-item__quantity-input'))
      .some((input) => input.value !== input.dataset.initialValue);
}

async function waitForCart() {
  const deadline = Date.now() + 12000;
  while (cartIsChanging()) {
    if (Date.now() > deadline) throw new Error('Please wait for your cart quantity to finish updating, then try again.');
    await new Promise((resolve) => setTimeout(resolve, 120));
  }
}

async function refreshCartSections() {
  document.dispatchEvent(new CustomEvent('theme:cartchanged', {
    detail: { bbPreserveAcknowledgement: true },
  }));
  // The theme marks each cart-form as refreshing synchronously, then emits
  // bb:cart-rendered after merging fresh rows and their new Shopify line keys.
  await waitForCart();
}

async function saveSnapshots(approvedState, writes) {
  let state = approvedState;
  // Property writes change line keys and may merge equivalent lines. Re-read
  // before each write; use line position from that exact snapshot, never an old key.
  for (let attempts = 0; attempts < 100; attempts += 1) {
    const index = state.lines.findIndex((line) => !sameProperties(line.properties, backorderProperties(line, true)));
    if (index < 0) return state;
    const line = state.lines[index];
    // A failed network response can still follow a successful server write.
    // Refresh real cart rows on either success or failure before allowing edits.
    writes.attempted = true;
    const response = await fetch(`${cartUrl}/change.js`, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: index + 1, quantity: line.quantity, properties: backorderProperties(line, true) }),
    });
    if (!response.ok) throw new Error('Your backorder details could not be saved. Please try checkout again.');
    state = await readSnapshot();
    if (state.context !== approvedState.context) return state;
  }
  throw new Error('Your cart is still changing. Please review it and try checkout again.');
}

async function prepareCheckout(replay, control) {
  if (checkoutBusy) return;
  checkoutBusy = true;
  const resolveControl = resolveCheckoutControl(control);
  const writes = { attempted: false };
  let sectionRefreshAttempted = false;
  control?.setAttribute('aria-busy', 'true');
  clearTimeout(refreshTimer);
  ++refreshVersion;
  try {
    await waitForCart();
    lockCartInputs();
    const state = await readSnapshot();
    useState(state);
    if (state.requiresAcknowledgement && acceptedContext !== state.context) {
      showError('Please confirm that you understand the backorder arrival and dispatch timing before continuing.', true);
      return;
    }
    let updated = await saveSnapshots(state, writes);
    useState(updated);
    if (writes.attempted) {
      sectionRefreshAttempted = true;
      await refreshCartSections();
      updated = await readSnapshot();
      useState(updated);
    }
    if (updated.context !== state.context ||
        (updated.requiresAcknowledgement && acceptedContext !== updated.context)) {
      acceptedContext = null;
      renderCart();
      showError('Your cart or stock availability changed. Please review the updated backorder details and confirm again.', true);
      return;
    }
    showError('');
    // Native cart submission must include its quantity inputs, and existing
    // depot/terms click handlers must still run on the current checkout control.
    restoreCartInputs();
    replay(resolveControl());
  } catch (error) {
    if (writes.attempted && !sectionRefreshAttempted) {
      sectionRefreshAttempted = true;
      try {
        await refreshCartSections();
        useState(await readSnapshot());
      } catch {
        acceptedContext = null;
        error = new Error('Your cart details could not be refreshed. Please reload the page before trying checkout again.');
      }
    }
    showError(error.message || 'We could not check availability. Please try again.', true);
  } finally {
    checkoutBusy = false;
    restoreCartInputs();
    control?.removeAttribute('aria-busy');
  }
}

function checkoutControl(target) {
  const control = target.closest('button[name="checkout"], input[name="checkout"], a[href], [data-bb-backorder-checkout]');
  if (!control) return null;
  if (control.name === 'checkout') return control;
  if (control instanceof HTMLAnchorElement && /\/checkout\/?$/.test(new URL(control.href, location.href).pathname)) return control;
  return null;
}

document.addEventListener('click', (event) => {
  if (checkoutBusy && event.target.closest('.cart-item__remove, .quantity-up, .quantity-down, .quick-add, button[name="update"]')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }
  const control = checkoutControl(event.target);
  if (!control || replayControls.has(control)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  prepareCheckout((freshControl) => replayCheckout(freshControl), control);
}, true);

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (checkoutBusy && !replayForms.has(form) && (form.matches(managedForms) || form.matches('form.cart-form'))) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }
  if (form.matches(managedForms)) {
    updateProduct(form.closest(scopes));
    return;
  }
  if (!form.matches('form.cart-form') || replayForms.has(form)) return;
  if (event.submitter && event.submitter.name !== 'checkout') return;
  event.preventDefault();
  event.stopImmediatePropagation();
  const submitter = event.submitter || form.querySelector('[name="checkout"]');
  prepareCheckout((freshControl) => replayCheckout(freshControl, form), submitter);
}, true);

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-bb-backorder-accept]')) {
    acceptedContext = event.target.checked && currentState?.requiresAcknowledgement ? currentState.context : null;
    renderCart();
    showError('');
  }
  const scope = event.target.closest(scopes);
  if (scope) queueMicrotask(() => updateProduct(scope));
}, true);
document.addEventListener('input', (event) => {
  const scope = event.target.closest(scopes);
  if (scope && event.target.name === 'quantity') updateProduct(scope);
});
document.addEventListener('theme:cartchanged', (event) => {
  if (event.detail?.bbPreserveAcknowledgement !== true) acceptedContext = null;
  queueRefresh();
});
document.addEventListener('bb:cart-rendered', () => {
  if (checkoutBusy) lockCartInputs();
  renderCart();
  queueRefresh();
});
document.addEventListener('theme:open-cart-drawer', queueRefresh);
window.addEventListener('pageshow', queueRefresh);

if (window.jQuery) {
  window.jQuery(document).on('variantChanged.bbBackorders', scopes, function () { updateProduct(this); });
  window.jQuery(document).on('change.bbBackorders', `${scopes} [name="quantity"]`, function () { updateProduct(this.closest(scopes)); });
}

// Quick-buy forms and recommendations can be inserted after page load.
new MutationObserver((mutations) => {
  if (checkoutBusy) lockCartInputs();
  if (renderScheduled || !mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
    node.nodeType === 1 && (node.matches?.(`${scopes}, [data-bb-backorder-ack]`) || node.querySelector?.(`${scopes}, [data-bb-backorder-ack]`))
  ))) return;
  renderScheduled = true;
  requestAnimationFrame(() => { renderScheduled = false; renderProducts(); renderCart(); });
}).observe(document.body, { childList: true, subtree: true });

renderProducts();
refreshCart().catch(() => {});
